import { formatResultDateTime } from '@/lib/dateTime';

import { KeyValueList, KeyValueRow, SubCard } from '../../components/detail/Accordion';
import { parseEmailVerificationResult } from './email.utils';

/**
 * A completed Email Verification step, on the admin's session view.
 *
 * Built from the page's own `SubCard` / `KeyValueList` / `KeyValueRow`, like every other step's
 * Detail card. This step has no API catalogue entry, so `StepAccordion`'s catalogue-driven field
 * resolution finds no spec for it and would render an empty body — the same reason
 * `QuestionnaireAnswersView` exists.
 *
 * Every row is conditional because Return Data can switch any field off. A row that says
 * "Not returned" would be misleading here: the field was not withheld, the tenant chose not to
 * collect it.
 */
export function EmailVerificationResultView({ resultJson }: { resultJson: string | null }) {
  const result = parseEmailVerificationResult(resultJson);

  const hasAnything =
    result.email_address !== undefined ||
    result.verified !== undefined ||
    result.verified_at !== undefined ||
    result.codes_sent !== undefined;

  if (!hasAnything) {
    return (
      <p className="text-[length:var(--text-14)] text-[#666666]">
        This step has no recorded result yet.
      </p>
    );
  }

  return (
    <SubCard title="Email Verification">
      <KeyValueList>
        {result.email_address && (
          // The FULL address here, not the masked one the applicant saw. This is the admin's record
          // of what was verified, and a masked value would make the step useless for review.
          <KeyValueRow label="Email Address:" value={result.email_address} />
        )}

        {result.verified !== undefined && (
          <KeyValueRow
            label="Status:"
            value={
              <span className={result.verified ? 'text-green00' : 'text-orangeDA'}>
                {result.verified ? 'Verified' : 'Not verified'}
              </span>
            }
          />
        )}

        {result.verified_at && (
          <KeyValueRow label="Verified At:" value={formatResultDateTime(result.verified_at)} />
        )}

        {result.codes_sent !== undefined && (
          // Worth surfacing: one code to one confirmation is the normal story, and a dozen sends is
          // a different one — a mistyped address, or someone who could not reach the mailbox.
          <KeyValueRow label="Codes Sent:" value={String(result.codes_sent)} />
        )}
      </KeyValueList>
    </SubCard>
  );
}
